import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * MB-A AttachmentList
 *
 * Lists briefing attachments for a project.
 * - Download: GET /v1/attachments/:aid/download  (everyone)
 * - Upload:   POST /v1/projects/:pid/attachments  (canBriefingManage)
 * - Delete:   DELETE /v1/attachments/:aid         (canBriefingManage, with confirm)
 *
 * Client-side hints for allowed types/sizes are informational only; enforcement
 * is server-side (handles 413/415 with friendly i18n messages).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { Card } from './card.js';
import { useConfirm } from './confirm-dialog.js';

export type AttachmentListProps = {
  projectId: string;
  canBriefingManage: boolean;
  /**
   * 'full' (Default): Liste + Upload + Löschen (Briefing-Tab).
   * 'compact': nur Liste + Ansehen/Download/SF-Link (rechte Sidebar) — keine
   * Verwaltung, read-only.
   */
  variant?: 'full' | 'compact';
  /**
   * Lightning-Host der Salesforce-Instanz (z. B.
   * https://mindsquare.lightning.force.com). Aktiviert für SF-Anhänge einen
   * Lightning-Deeplink ins Quellsystem — vor allem für Formate ohne
   * Inline-Vorschau (DOCX/PPTX/XLSX), die dort eine native Vorschau haben.
   * null/undefined → kein SF-Link.
   */
  salesforceLightningBaseUrl?: string | null;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Typen, die der Browser nativ im Tab rendern kann (PDF/Bilder). Office-Formate
// (DOCX/PPTX/XLSX) brauchen einen externen Viewer mit öffentlicher URL und sind
// hier bewusst NICHT enthalten — sie bleiben Download.
const PREVIEWABLE_MIME = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

function canPreview(mime: string | null): boolean {
  return mime !== null && PREVIEWABLE_MIME.has(mime);
}

export function AttachmentList({
  projectId,
  canBriefingManage,
  variant = 'full',
  salesforceLightningBaseUrl,
}: AttachmentListProps) {
  const isCompact = variant === 'compact';
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const attachmentsQuery = useQuery({
    queryKey: ['v2-attachments', projectId],
    queryFn: () => api.listAttachments(projectId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['v2-attachments', projectId] });

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(projectId, file),
    onSuccess: () => {
      setUploadError(null);
      toast.success(t('v2.attachment.upload_success', 'Datei hochgeladen'));
      void invalidate();
    },
    onError: (err) => {
      let msg: string;
      if (err instanceof ApiError) {
        if (err.status === 413) {
          msg = t('v2.attachment.error_too_large', 'Datei zu groß (max. 20 MB).');
        } else if (err.status === 415) {
          msg = t('v2.attachment.error_type', 'Dateityp nicht erlaubt.');
        } else {
          msg = err.message;
        }
      } else {
        msg =
          err instanceof Error
            ? err.message
            : t('v2.attachment.error_generic', 'Upload fehlgeschlagen.');
      }
      setUploadError(msg);
      toast.error(t('v2.attachment.upload_failed', 'Upload fehlgeschlagen'), msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteAttachment(id),
    onSuccess: () => {
      toast.success(t('v2.attachment.delete_success', 'Anhang gelöscht'));
      void invalidate();
    },
    onError: (err) =>
      toast.error(
        t('v2.attachment.delete_failed', 'Löschen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    uploadMut.mutate(file);
    // Reset input so same file can be re-uploaded after delete
    e.target.value = '';
  }

  const items = attachmentsQuery.data?.items ?? [];

  const inner = (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4
          className="text-[11px] uppercase tracking-[0.12em]"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.attachment.section_title', 'Anhänge')}
          {items.length > 0 && (
            <span
              className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink-3)',
                fontFamily: 'var(--f-mono)',
              }}
            >
              {items.length}
            </span>
          )}
        </h4>
        {canBriefingManage && !isCompact && (
          <div className="flex items-center gap-2">
            <span
              className="text-[10px]"
              style={{ color: 'var(--ink-4)', fontFamily: 'var(--f-mono)' }}
            >
              {t('v2.attachment.hint_types', 'PDF, DOCX, PPTX, XLSX, PNG, JPG · max 20 MB')}
            </span>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMut.isPending}
              className="text-xs px-3 py-1.5 rounded-md"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink)',
                border: '1px solid var(--line-soft)',
                opacity: uploadMut.isPending ? 0.5 : 1,
                cursor: uploadMut.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {uploadMut.isPending
                ? t('v2.attachment.uploading', 'Lädt hoch…')
                : t('v2.attachment.upload_btn', '↑ Hochladen')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.pptx,.xlsx,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={handleFileChange}
              aria-label={t('v2.attachment.file_input_aria', 'Datei für Upload auswählen')}
            />
          </div>
        )}
      </div>

      {uploadError && !isCompact && (
        <div
          className="text-xs px-3 py-2 rounded-md"
          style={{
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            border: '1px solid var(--danger)',
          }}
        >
          {uploadError}
        </div>
      )}

      {attachmentsQuery.isLoading &&
        (isCompact ? (
          <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.loading', 'Lädt…')}
          </p>
        ) : (
          <Card>
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              {t('v2.detail.loading', 'Lädt…')}
            </p>
          </Card>
        ))}

      {!attachmentsQuery.isLoading && items.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.attachment.empty', 'Keine Anhänge.')}
        </p>
      )}

      {!attachmentsQuery.isLoading && items.length > 0 && (
        <ListShell isCompact={isCompact}>
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {items.map((att) => (
              <li
                key={att.attachment_id}
                className="px-4 py-2.5 flex items-center gap-3 group"
                style={{ borderColor: 'var(--line-soft)' }}
              >
                {/* Source badge */}
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
                  style={{
                    background:
                      att.source === 'salesforce' ? 'var(--primary-soft)' : 'var(--surface-2)',
                    color: att.source === 'salesforce' ? 'var(--primary)' : 'var(--ink-3)',
                    fontFamily: 'var(--f-mono)',
                  }}
                >
                  {att.source === 'salesforce'
                    ? t('v2.attachment.source_sf', 'SF')
                    : t('v2.attachment.source_upload', 'Upload')}
                </span>

                {/* Filename + size */}
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm truncate"
                    style={{ color: 'var(--ink)' }}
                    title={att.file_name}
                  >
                    {att.file_name}
                  </div>
                  <div
                    className="text-[10px]"
                    style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
                  >
                    {formatBytes(att.size_bytes)}
                    {att.mime_type ? ` · ${att.mime_type}` : ''}
                  </div>
                </div>

                {/* Ansehen (nur PDF/Bilder) — öffnet inline im neuen Tab */}
                {canPreview(att.mime_type) && (
                  <a
                    href={api.getAttachmentViewUrl(att.attachment_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-2 py-1 rounded shrink-0"
                    style={{ color: 'var(--primary)', background: 'none', textDecoration: 'none' }}
                    title={t('v2.attachment.view', 'Im Browser ansehen')}
                  >
                    ↗
                  </a>
                )}

                {/* SF-Lightning-Deeplink (Formate ohne Inline-Vorschau, z. B. */}
                {/* Office) — öffnet die Datei-Detailseite im Lightning-Quellsystem */}
                {/* (Klick auf die Datei dort zeigt die Vorschau). Muster: */}
                {/* /lightning/r/ContentDocument/<ContentDocumentId>/view (069…). */}
                {!canPreview(att.mime_type) &&
                  att.source === 'salesforce' &&
                  salesforceLightningBaseUrl &&
                  att.sf_content_document_id && (
                    <a
                      href={`${salesforceLightningBaseUrl}/lightning/r/ContentDocument/${att.sf_content_document_id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 rounded shrink-0"
                      style={{
                        color: 'var(--primary)',
                        background: 'none',
                        textDecoration: 'none',
                      }}
                      title={t('v2.attachment.view_sf', 'In Salesforce ansehen')}
                    >
                      ↗
                    </a>
                  )}

                {/* Download */}
                <a
                  href={api.getAttachmentDownloadUrl(att.attachment_id)}
                  download={att.file_name}
                  className="text-xs px-2 py-1 rounded shrink-0"
                  style={{ color: 'var(--primary)', background: 'none', textDecoration: 'none' }}
                  title={t('v2.attachment.download', 'Herunterladen')}
                >
                  ↓
                </a>

                {/* Delete (gated) — nur in der vollen Verwaltungsansicht */}
                {canBriefingManage && !isCompact && (
                  <button
                    type="button"
                    onClick={() => {
                      void confirmAction({
                        title: t(
                          'v2.attachment.delete_confirm',
                          'Anhang „{{name}}" wirklich löschen?',
                          { name: att.file_name },
                        ),
                        tone: 'danger',
                        confirmLabel: t('common.delete', 'Löschen'),
                      }).then((ok) => {
                        if (ok) deleteMut.mutate(att.attachment_id);
                      });
                    }}
                    disabled={deleteMut.isPending}
                    className="text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    style={{
                      color: 'var(--danger)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title={t('v2.attachment.delete_btn', 'Anhang löschen')}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </ListShell>
      )}
    </div>
  );

  // Compact (rechte Sidebar): wie Staffing/Sync ein Card-Rahmen um den GANZEN Block
  // (auch im Leerzustand) — nicht erst um die einzelnen Files. Full-Variante (Briefing-Tab)
  // bleibt rahmenlos, dort rahmt nur die Datei-Liste.
  return isCompact ? <Card>{inner}</Card> : inner;
}

/**
 * Rahmen um die Datei-Liste: in der Full-Variante eine eigene Card; in der Compact-Variante
 * rahmenlos, weil der äußere Block bereits in einer Card steckt (kein Rahmen-im-Rahmen).
 */
function ListShell({ isCompact, children }: { isCompact: boolean; children: React.ReactNode }) {
  if (isCompact) return <>{children}</>;
  return <Card padding={false}>{children}</Card>;
}
